<script setup>
import { ref, computed, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
  CMD_SEND_CONVERSATION_TO_PROVET,
} from 'dashboard/helper/commandbar/events';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PaymentForm from 'dashboard/routes/dashboard/conversation/PaymentForm.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import ProvetAPI from 'dashboard/api/provet';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const bookingLinkLoading = ref(false);
const reviewReminderLoading = ref(false);
const removeFromSurveyLoading = ref(false);
const enrolInSurveyLoading = ref(false);
const registrationLinkLoading = ref(false);
const syncClientLoading = ref(false);
const paymentDialogRef = ref(null);
const addClientIdDialogRef = ref(null);
const addClientConfirmDialogRef = ref(null);
const sendToProvetDialogRef = ref(null);
const reviewReminderDialogRef = ref(null);
const removeFromSurveyDialogRef = ref(null);
const enrolInSurveyDialogRef = ref(null);
const addClientIdLoading = ref(false);
const addClientPreviewLoading = ref(false);
const manualClientId = ref('');
const addClientIdError = ref('');
const clientPreview = ref(null);
const sendToProvetFrom = ref('');
const sendToProvetTo = ref('');

const [showStatusMenu, toggleStatusMenu] = useToggle();
const [showActionsMenu, toggleActionsMenu] = useToggle();

const closeStatusMenu = () => toggleStatusMenu(false);
const openStatusMenu = () => {
  toggleActionsMenu(false);
  toggleStatusMenu(true);
};

const closeActionsMenu = () => toggleActionsMenu(false);
const openActionsMenu = () => {
  if (!showAdditionalActions.value) return;
  toggleStatusMenu(false);
  toggleActionsMenu(true);
};

const currentChat = computed(() => getters.getSelectedChat.value);
const showSendReviewReminder = computed(
  () => store.getters['globalConfig/get']?.sendReviewReminderEnabled !== false
);

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

watch(showAdditionalActions, enabled => {
  if (!enabled) closeActionsMenu();
});

watch(manualClientId, () => {
  addClientIdError.value = '';
});

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const contactId = computed(() => currentChat.value?.meta?.sender?.id);

const currentContact = computed(() =>
  store.getters['contacts/getContact'](currentChat.value?.meta?.sender?.id)
);

const contactHasClientId = contact => {
  if (!contact) return false;
  return Boolean(contact.client_id || contact.clientId);
};

const conversationContact = computed(
  () => currentContact.value || currentChat.value?.meta?.sender || {}
);

const hasLinkedClient = computed(
  () =>
    contactHasClientId(currentContact.value) ||
    contactHasClientId(currentChat.value?.meta?.sender)
);

const isSurveyProhibited = computed(() => {
  const contacts = [currentContact.value, currentChat.value?.meta?.sender];
  return contacts.some(
    contact => (contact?.survey_prohibited ?? contact?.surveyProhibited) === true
  );
});

const onShowPaymentForm = () => {
  if (!contactId.value) {
    useAlert('No contact associated with this conversation');
    return;
  }
  closeActionsMenu();
  paymentDialogRef.value?.open();
};

const openRemoveFromSurveyConfirm = () => {
  if (
    !conversationContact.value?.id ||
    !hasLinkedClient.value ||
    isSurveyProhibited.value ||
    removeFromSurveyLoading.value
  ) {
    return;
  }

  closeActionsMenu();
  removeFromSurveyDialogRef.value?.open();
};

const onRemoveFromSurvey = async () => {
  const id = conversationContact.value?.id;
  if (!id || removeFromSurveyLoading.value) return;

  removeFromSurveyLoading.value = true;
  try {
    await store.dispatch('contacts/removeFromSurvey', id);
    removeFromSurveyDialogRef.value?.close();
    useAlert(t('CONTACT_PANEL.REMOVE_FROM_SURVEY_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.REMOVE_FROM_SURVEY_ERROR'));
  } finally {
    removeFromSurveyLoading.value = false;
  }
};

const openEnrolInSurveyConfirm = () => {
  if (
    !conversationContact.value?.id ||
    !hasLinkedClient.value ||
    !isSurveyProhibited.value ||
    enrolInSurveyLoading.value
  ) {
    return;
  }

  closeActionsMenu();
  enrolInSurveyDialogRef.value?.open();
};

const onEnrolInSurvey = async () => {
  const id = conversationContact.value?.id;
  if (!id || enrolInSurveyLoading.value) return;

  enrolInSurveyLoading.value = true;
  try {
    await store.dispatch('contacts/enrolInSurvey', id);
    enrolInSurveyDialogRef.value?.close();
    useAlert(t('CONTACT_PANEL.ENROL_IN_SURVEY_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACT_PANEL.ENROL_IN_SURVEY_ERROR'));
  } finally {
    enrolInSurveyLoading.value = false;
  }
};

const openReviewReminderConfirm = () => {
  if (!currentChat.value?.id || reviewReminderLoading.value) return;

  closeActionsMenu();
  reviewReminderDialogRef.value?.open();
};

const onSendReviewReminder = async () => {
  const conversationId = currentChat.value?.id;
  if (!conversationId || reviewReminderLoading.value) return;

  reviewReminderLoading.value = true;
  try {
    await ConversationApi.sendReviewReminder(conversationId);
    reviewReminderDialogRef.value?.close();
    useAlert(t('CONVERSATION.HEADER.SEND_REVIEW_REMINDER_SUCCESS'));
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION.HEADER.SEND_REVIEW_REMINDER_FAILED');
    useAlert(message);
  } finally {
    reviewReminderLoading.value = false;
  }
};

const onInsertBookingLinkIntoReply = async (isRegistration = false) => {
  const conversationId = currentChat.value?.id;
  if (!conversationId) {
    useAlert(t('CONVERSATION.HEADER.BOOKING_LINK_NO_CONVERSATION'));
    return;
  }
  const pathSegment = isRegistration ? 'registration' : 'booking';
  const loadingRef = isRegistration ? registrationLinkLoading : bookingLinkLoading;
  loadingRef.value = true;
  try {
    const { data } = await ConversationApi.getBookingLinkMessage({
      conversationId,
      pathSegment,
    });
    if (data?.content) {
      closeActionsMenu();
      emitter.emit(BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR, data.content);
    }
  } catch (error) {
    const fallbackKey = isRegistration
      ? 'CONVERSATION.HEADER.REGISTRATION_LINK_FAILED'
      : 'CONVERSATION.HEADER.BOOKING_LINK_FAILED';
    const message = error?.response?.data?.error || t(fallbackKey);
    useAlert(message);
  } finally {
    loadingRef.value = false;
  }
};

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  closeStatusMenu();
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = (status, snoozedUntil) => {
  closeStatusMenu();
  isLoading.value = true;
  store
    .dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status,
      snoozedUntil,
    })
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
      isLoading.value = false;
    });
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
};

const LONDON_TIMEZONE = 'Europe/London';

const formatForDateTimeLocalInput = date => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const get = type => parts.find(part => part.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
};

const setSendToProvetDefaultRange = () => {
  const now = new Date();
  const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  sendToProvetTo.value = formatForDateTimeLocalInput(now);
  sendToProvetFrom.value = formatForDateTimeLocalInput(from);
};

const openSendToProvetDialog = () => {
  closeActionsMenu();
  setSendToProvetDefaultRange();
  sendToProvetDialogRef.value?.open();
};

const onCmdSendConversationToProvet = () => {
  const fromValue = sendToProvetFrom.value || undefined;
  const toValue = sendToProvetTo.value || undefined;
  if (fromValue && toValue && fromValue > toValue) {
    useAlert(t('CONVERSATION.HEADER.SEND_TO_PROVET_INVALID_RANGE'));
    return;
  }

  sendToProvetDialogRef.value?.close();
  isLoading.value = true;
  store
    .dispatch('sendToProvet', {
      conversationId: currentChat.value.id,
      from: fromValue,
      to: toValue,
    })
    .then(() => {
      useAlert(t('CONVERSATION.HEADER.SEND_TO_PROVET_SUCCESS'));
      isLoading.value = false;
    })
    .catch(() => {
      useAlert(t('CONVERSATION.HEADER.SEND_TO_PROVET_FAILED'));
      isLoading.value = false;
    });
};

const onSyncClientInfoWithProvet = async () => {
  const clientId = currentContact.value?.client_id || currentContact.value?.clientId;
  if (!clientId) return;

  closeActionsMenu();
  syncClientLoading.value = true;
  try {
    await ProvetAPI.sync({ clientId });
    useAlert(t('CONVERSATION.HEADER.NUMBER_ADDED_TO_PROVET'));
    window.location.reload();
  } catch (error) {
    const message = error?.response?.data?.error || 'Failed to sync client with Provet. Please try again.';
    useAlert(message);
  } finally {
    syncClientLoading.value = false;
  }
};

const isManualClientIdValid = computed(() => Number(manualClientId.value) > 0);
const currentContactPhone = computed(
  () => currentContact.value?.phone_number || currentContact.value?.phoneNumber
);

const openAddClientIdModal = () => {
  closeActionsMenu();
  manualClientId.value = '';
  addClientIdError.value = '';
  clientPreview.value = null;
  addClientIdDialogRef.value?.open();
};

const onProceedToAddClientConfirm = async () => {
  const clientId = Number(manualClientId.value);
  if (clientId <= 0) return;

  addClientIdError.value = '';
  addClientPreviewLoading.value = true;
  try {
    const { data } = await ProvetAPI.clientPreview({ clientId });
    clientPreview.value = data;
    addClientIdDialogRef.value?.close();
    addClientConfirmDialogRef.value?.open();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404) {
      addClientIdError.value = t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET_CLIENT_NOT_FOUND');
    } else {
      addClientIdError.value =
        error?.response?.data?.error || 'Failed to load client details. Please try again.';
    }
  } finally {
    addClientPreviewLoading.value = false;
  }
};

const onBackFromAddClientConfirm = () => {
  addClientConfirmDialogRef.value?.close();
  addClientIdDialogRef.value?.open();
};

const onAddClientId = async () => {
  const clientId = Number(manualClientId.value);
  if (clientId <= 0 || !currentContactPhone.value) {
    useAlert('Contact phone number is required');
    return;
  }

  addClientIdLoading.value = true;
  try {
    await ProvetAPI.addClientPhone({
      clientId,
      phone: currentContactPhone.value,
    });
    await ProvetAPI.sync({ clientId });
    useAlert('Number added to Provet successfully');
    addClientConfirmDialogRef.value?.close();
    window.location.reload();
  } catch (error) {
    const message = error?.response?.data?.error || 'Failed to sync client with Provet. Please try again.';
    useAlert(message);
  } finally {
    addClientIdLoading.value = false;
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
useEmitter(CMD_SEND_CONVERSATION_TO_PROVET, onCmdSendConversationToProvet);
</script>

<template>
  <div class="relative flex items-center justify-end gap-2 resolve-actions">
    <div v-on-clickaway="closeActionsMenu" class="relative flex-shrink-0">
      <Button :label="t('CONVERSATION.HEADER.ACTIONS')" size="sm" color="slate" trailing-icon
        icon="i-lucide-chevron-down" :disabled="isLoading || !showAdditionalActions" @click="openActionsMenu" />
      <div v-if="showActionsMenu"
        class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-max min-w-max z-20 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 end-0 [&_ul>li]:mb-0">
        <WootDropdownMenu class="mb-0">
          <WootDropdownItem>
            <Button :label="t('CONVERSATION.HEADER.CREATE_PAYMENT')" ghost slate sm start icon="i-lucide-credit-card"
              class="w-full" :is-loading="isLoading" @click="onShowPaymentForm" />
          </WootDropdownItem>
          <WootDropdownItem>
            <Button :label="t('CONVERSATION.HEADER.SEND_BOOKING_LINK')" ghost slate sm start icon="i-lucide-link"
              class="w-full" :is-loading="bookingLinkLoading" @click="onInsertBookingLinkIntoReply(false)" />
          </WootDropdownItem>
          <WootDropdownItem>
            <Button :label="t('CONVERSATION.HEADER.SEND_REGISTRATION_LINK')" ghost slate sm start icon="i-lucide-link"
              class="w-full" :is-loading="registrationLinkLoading" @click="onInsertBookingLinkIntoReply(true)" />
          </WootDropdownItem>
          <WootDropdownItem v-if="contactHasClientId(currentContact)">
            <Button :label="t('CONVERSATION.HEADER.SEND_TO_PROVET')" ghost slate sm start icon="i-lucide-send"
              class="w-full" :is-loading="isLoading" @click="openSendToProvetDialog" />
          </WootDropdownItem>
          <WootDropdownItem v-if="contactHasClientId(currentContact)">
            <Button :label="t('CONVERSATION.HEADER.SYNC_CLIENT_INFO_WITH_PROVET')" ghost slate sm start
              icon="i-lucide-refresh-cw" class="w-full" :is-loading="syncClientLoading"
              @click="onSyncClientInfoWithProvet" />
          </WootDropdownItem>
          <WootDropdownItem v-if="!contactHasClientId(currentContact)">
            <Button :label="t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET')" ghost slate sm start icon="i-lucide-plus"
              class="w-full" @click="openAddClientIdModal" />
          </WootDropdownItem>
          <WootDropdownItem v-if="showSendReviewReminder">
            <Button :label="t('CONVERSATION.HEADER.SEND_REVIEW_REMINDER')" ghost slate sm start icon="i-lucide-star"
              class="w-full" :is-loading="reviewReminderLoading" @click="openReviewReminderConfirm" />
          </WootDropdownItem>
          <WootDropdownItem v-if="hasLinkedClient && !isSurveyProhibited">
            <Button :label="t('CONTACT_PANEL.REMOVE_FROM_SURVEY')" ghost slate sm start icon="i-lucide-clipboard-x"
              class="w-full" :is-loading="removeFromSurveyLoading" :disabled="removeFromSurveyLoading"
              @click="openRemoveFromSurveyConfirm" />
          </WootDropdownItem>
          <WootDropdownItem v-if="hasLinkedClient && isSurveyProhibited">
            <Button :label="t('CONTACT_PANEL.ENROL_IN_SURVEY')" ghost slate sm start icon="i-lucide-clipboard-check"
              class="w-full" :is-loading="enrolInSurveyLoading" :disabled="enrolInSurveyLoading"
              @click="openEnrolInSurveyConfirm" />
          </WootDropdownItem>
        </WootDropdownMenu>
      </div>
    </div>
    <div class="rounded-lg shadow outline-1 outline flex-shrink-0"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'">

      <Button v-if="isOpen" :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')" size="sm" color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0" :is-loading="isLoading"
        @click="onCmdResolveConversation" />
      <Button v-else-if="isResolved" :label="t('CONVERSATION.HEADER.REOPEN_ACTION')" size="sm" color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0" :is-loading="isLoading"
        @click="onCmdOpenConversation" />
      <Button v-else-if="showOpenButton" :label="t('CONVERSATION.HEADER.OPEN_ACTION')" size="sm" color="slate"
        :is-loading="isLoading" @click="onCmdOpenConversation" />
      <Button v-if="showAdditionalActions" ref="arrowDownButtonRef" icon="i-lucide-chevron-down" :disabled="isLoading"
        size="sm" class="ltr:rounded-l-none rtl:rounded-r-none !outline-0" color="slate" trailing-icon
        @click="openStatusMenu" />
    </div>
    <div v-if="showStatusMenu" v-on-clickaway="closeStatusMenu"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-20 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0">
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')" ghost slate sm start
            icon="i-lucide-alarm-clock-minus" class="w-full" @click="() => openSnoozeModal()" />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')" ghost slate sm start
            icon="i-lucide-circle-dot-dashed" class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)" />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <Dialog ref="paymentDialogRef" type="edit" :title="t('CONVERSATION.HEADER.CREATE_PAYMENT')"
      :show-cancel-button="false" :show-confirm-button="false" width="md">
      <template v-if="contactId">
        <PaymentForm :contact-id="contactId" @close="() => paymentDialogRef?.close()"
          @created="() => paymentDialogRef?.close()" />
      </template>
      <template v-else>
        <p class="text-n-slate-11">No contact associated with this conversation.</p>
      </template>
    </Dialog>
    <Dialog ref="addClientIdDialogRef" type="edit" :title="t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET_DIALOG_TITLE')"
      :show-cancel-button="false" :show-confirm-button="false" width="md">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm text-n-slate-11">
            {{ t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET_CLIENT_ID_LABEL') }}
          </label>
          <Input v-model="manualClientId" type="number" min="1" placeholder="1234" class="w-full" />
        </div>
        <p v-if="addClientIdError" class="text-sm text-n-ruby-11">
          {{ addClientIdError }}
        </p>
        <p class="text-n-slate-11">
          {{ t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET_DIALOG_DESCRIPTION') }}
        </p>
      </div>
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button label="Cancel" variant="faded" color="slate" class="w-full" @click="addClientIdDialogRef?.close()" />
          <Button label="Continue" color="blue" class="w-full" :is-loading="addClientPreviewLoading"
            :disabled="!isManualClientIdValid || addClientPreviewLoading" @click="onProceedToAddClientConfirm" />
        </div>
      </template>
    </Dialog>
    <Dialog ref="addClientConfirmDialogRef" type="edit"
      :title="t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET_CONFIRM_TITLE')" :show-cancel-button="false"
      :show-confirm-button="false" width="md">
      <div v-if="clientPreview" class="flex flex-col gap-3">
        <p>
          <span class="font-semibold">Client:</span>
          {{ clientPreview.client.name }} ({{ clientPreview.client.id }})
        </p>
        <p v-for="(patient, index) in clientPreview.patients" :key="patient.id">
          <span class="font-semibold">Patient {{ index + 1 }}:</span>
          {{ patient.name }}, {{ patient.species }} ({{ patient.id }})
        </p>
        <p class="text-sm text-n-slate-11">
          {{ t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET_CONFIRM_HINT') }}
        </p>
      </div>
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button :label="t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET_CONFIRM_BACK')" variant="faded" color="slate"
            class="w-full" :disabled="addClientIdLoading" @click="onBackFromAddClientConfirm" />
          <Button :label="t('CONVERSATION.HEADER.ADD_NUMBER_TO_PROVET_CONFIRM')" color="blue" class="w-full"
            :is-loading="addClientIdLoading" :disabled="addClientIdLoading" @click="onAddClientId" />
        </div>
      </template>
    </Dialog>
    <Dialog ref="reviewReminderDialogRef" type="edit" :title="t('CONVERSATION.HEADER.SEND_REVIEW_REMINDER')"
      width="md" :is-loading="reviewReminderLoading" @confirm="onSendReviewReminder">
      <template #description>
        <p class="mb-0 text-sm text-n-slate-11"
          v-dompurify-html="t('CONVERSATION.HEADER.SEND_REVIEW_REMINDER_CONFIRM')" />
      </template>
    </Dialog>
    <Dialog ref="removeFromSurveyDialogRef" type="alert" :title="t('CONTACT_PANEL.REMOVE_FROM_SURVEY')"
      :description="t('CONVERSATION.HEADER.REMOVE_FROM_SURVEY_CONFIRM')" width="md"
      :is-loading="removeFromSurveyLoading" @confirm="onRemoveFromSurvey" />
    <Dialog ref="enrolInSurveyDialogRef" type="alert" :title="t('CONTACT_PANEL.ENROL_IN_SURVEY')"
      :description="t('CONVERSATION.HEADER.ENROL_IN_SURVEY_CONFIRM')" width="md"
      :is-loading="enrolInSurveyLoading" @confirm="onEnrolInSurvey" />
    <Dialog ref="sendToProvetDialogRef" type="edit" :title="t('CONVERSATION.HEADER.SEND_TO_PROVET_DIALOG_TITLE')"
      :show-cancel-button="false" :show-confirm-button="false" width="md">
      <div class="flex flex-col gap-4">
        <Input v-model="sendToProvetFrom" type="datetime-local" :label="t('CONVERSATION.HEADER.SEND_TO_PROVET_FROM')" />
        <Input v-model="sendToProvetTo" type="datetime-local" :label="t('CONVERSATION.HEADER.SEND_TO_PROVET_TO')" />
      </div>
      <template #footer>
        <div class="flex items-center justify-between w-full gap-3">
          <Button :label="t('CONVERSATION.HEADER.SEND_TO_PROVET_CANCEL')" variant="faded" color="slate" class="w-full"
            @click="sendToProvetDialogRef?.close()" />
          <Button :label="t('CONVERSATION.HEADER.SEND_TO_PROVET_SAVE')" color="blue" class="w-full"
            :is-loading="isLoading" @click="onCmdSendConversationToProvet" />
        </div>
      </template>
    </Dialog>
  </div>
</template>
